// src/context/FavoritesContext.jsx
import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { toast } from 'react-toastify';
import { db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useAuth } from './AuthContext';

const FavoritesContext = createContext();
const GUEST_FAVORITES_KEY = 'sena_favorites';

export const useFavorites = () => useContext(FavoritesContext);

// Misafir favorileri ile hesap favorilerini birleştirir (id ile)
function mergeFavorites(accountItems = [], guestItems = []) {
  const merged = [...accountItems];
  guestItems.forEach((item) => {
    if (!merged.some((it) => it.id === item.id)) {
      merged.push(item);
    }
  });
  return merged;
}

export const FavoritesProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const syncedUidRef = useRef(null);

  // --- LOGIN / LOGOUT GEÇİŞLERİNİ YÖNET (ilk yükleme de buradan geçer) ---
  useEffect(() => {
    if (!currentUser) {
      syncedUidRef.current = null;
      try {
        const saved = localStorage.getItem(GUEST_FAVORITES_KEY);
        setFavorites(saved ? JSON.parse(saved) : []);
      } catch (e) {
        console.error("Favoriler yüklenemedi", e);
        setFavorites([]);
      }
      return;
    }

    if (syncedUidRef.current === currentUser.uid) return;

    let cancelled = false;
(async () => {
  try {
    const guestRaw = localStorage.getItem(GUEST_FAVORITES_KEY);
    const guestFavs = guestRaw ? JSON.parse(guestRaw) : [];

    const ref = doc(db, "favorites", currentUser.uid);
    const snap = await getDoc(ref);
    const accountFavs = snap.exists() && Array.isArray(snap.data().items) ? snap.data().items : [];

    const merged = mergeFavorites(accountFavs, guestFavs);

    // ✅ Önce Firestore'a yazmayı dene, ANCAK başarılı olursa misafir veriyi sil
    try {
      await setDoc(ref, { items: merged, updatedAt: Date.now() }, { merge: true });
      localStorage.removeItem(GUEST_FAVORITES_KEY);
    } catch (writeErr) {
      console.error("Favoriler Firestore'a yazılamadı, misafir veri korunuyor:", writeErr);
      // localStorage silinmedi → veri kaybolmaz, bir sonraki girişte tekrar denenir
    }

    if (cancelled) return;

    syncedUidRef.current = currentUser.uid;
    setFavorites(merged);
  } catch (e) {
    console.error("Favoriler senkronizasyonu başarısız:", e);
    syncedUidRef.current = currentUser.uid;
  }
})();

    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  // --- Üye girişliyken Firestore'a yaz ---
  useEffect(() => {
    if (!currentUser || syncedUidRef.current !== currentUser.uid) return;
    const ref = doc(db, "favorites", currentUser.uid);
    setDoc(ref, { items: favorites, updatedAt: Date.now() }, { merge: true }).catch((e) =>
      console.warn("Favoriler Firestore'a yazılamadı:", e)
    );
  }, [favorites, currentUser]);

  // --- Misafirken localStorage'a yaz ---
  useEffect(() => {
    if (currentUser) return;
    try {
      localStorage.setItem(GUEST_FAVORITES_KEY, JSON.stringify(favorites));
    } catch (e) {
      console.warn("Favoriler kaydedilemedi:", e);
    }
  }, [favorites, currentUser]);

  const toggleFavorite = (product) => {
    const isExist = favorites.some(item => item.id === product.id);
    let newFavs;

    if (isExist) {
      newFavs = favorites.filter(item => item.id !== product.id);
      toast.info("Ürün favorilerden çıkarıldı.", { autoClose: 1000 });
    } else {
      const favItem = {
        id: product.id,
        name: product.name,
        slug: product.slug || product.id,
        price: product.price,
        discountPercent: product.discountPercent || 0,
        discountActive: product.discountActive || false,
        image: product.imageUrls?.[0] || product.imageUrl || product.images?.[0] || "",
        brand: product.brand || ""
      };
      newFavs = [...favorites, favItem];
      toast.success("Ürün favorilere eklendi! ❤️", { autoClose: 1000 });
    }

    setFavorites(newFavs);
  };

  const isFavorite = (productId) => {
    return favorites.some(item => item.id === productId);
  };

  const clearFavorites = () => {
    setFavorites([]);
    toast.info("Favoriler listesi temizlendi.");
  };

  return (
    <FavoritesContext.Provider value={{ favorites, toggleFavorite, isFavorite, clearFavorites }}>
      {children}
    </FavoritesContext.Provider>
  );
};